import { useMemo, useState } from 'react'
import { ArrowRight, CheckCircle2, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import { getFriendlyError } from '@/utils/getFriendlyError'
import api from '@/services/api'
import type { Deal, DealStage } from '@/types'
import { mapDealFromApi, stagePatchPayload } from '@/adapters/deals'
import { formatDateTime } from '@/utils/format-date'
import { InfoField } from './InfoField'
import { QualificacaoSection } from './QualificacaoSection'
import { getLatestRealizedReuniao, getMissingBantFields } from './utils'

export const REUNIAO_REALIZADA_SECTION_ID = 'reuniao-realizada-section'

export function ReuniaoRealizadaSection({
  deal,
  onDealPatched,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
}) {
  const reuniao = useMemo(() => getLatestRealizedReuniao(deal), [deal])
  const [advancing, setAdvancing] = useState(false)

  const handleIniciarProposta = async () => {
    if (getMissingBantFields(deal).length > 0) {
      toast.error('Preencha a observação para avançar.')
      return
    }
    setAdvancing(true)
    try {
      await api.patch(`/deals/${deal.id}`, {
        budgetConfirmado: deal.budgetConfirmado,
        decisorPresente: deal.decisorPresente,
        doresIdentificadas: deal.doresIdentificadas,
      })

      const { data } = await api.patch(
        `/deals/${deal.id}/stage`,
        stagePatchPayload('proposta_elaboracao' as DealStage, {
          budgetConfirmado: deal.budgetConfirmado,
          decisorPresente: deal.decisorPresente,
          doresIdentificadas: deal.doresIdentificadas,
        }),
      )
      onDealPatched(mapDealFromApi(data.data ?? data))
      toast.success('Proposta liberada — etapa atualizada')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao iniciar proposta'))
    } finally {
      setAdvancing(false)
    }
  }

  const meetingDate = reuniao?.data ?? deal.dataReuniao

  return (
    <div
      id={REUNIAO_REALIZADA_SECTION_ID}
      className="deal-detail-hero-card glass-card scroll-mt-4 space-y-4 p-4"
    >
      <h3 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <CheckCircle2 size={14} />
        Reunião realizada
      </h3>

      {meetingDate && (
        <div className="rounded-md bg-white/[0.03] border border-white/[0.06] p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <InfoField label="Data da reunião" value={formatDateTime(meetingDate)} />
            <InfoField label="Tipo" value={reuniao?.tipo ?? 'Descoberta'} />
            <InfoField label="Closer" value={reuniao?.closerNome ?? deal.closerNome ?? '—'} />
            <InfoField label="Status" value={reuniao?.status ?? 'Realizada'} />
          </div>
          {(reuniao?.notas || reuniao?.transcricao) && (
            <div className="space-y-2 pt-2 border-t border-white/[0.06]">
              {reuniao?.notas && (
                <div>
                  <p className="text-[10px] text-text-label uppercase tracking-wider flex items-center gap-1 mb-1">
                    <MessageSquare size={11} />
                    Notas
                  </p>
                  <p className="text-sm text-text-secondary whitespace-pre-wrap">{reuniao.notas}</p>
                </div>
              )}
              {reuniao?.transcricao && (
                <div>
                  <p className="text-[10px] text-text-label uppercase tracking-wider mb-1">
                    Transcrição
                  </p>
                  <p className="text-sm text-text-secondary whitespace-pre-wrap line-clamp-4">
                    {reuniao.transcricao}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <QualificacaoSection deal={deal} onDealPatched={onDealPatched} required embedded />

      <Button
        variant="primary"
        size="sm"
        icon={<ArrowRight size={13} />}
        loading={advancing}
        onClick={handleIniciarProposta}
        className="w-full"
      >
        Iniciar proposta
      </Button>
    </div>
  )
}
